import { useEffect, useState } from "react";
import { Icon } from "./Icon";

/** The OS setting or the person's own preference (lib/preferences.ts); either stops the glide. */
function reducedMotion(): boolean {
  if (document.documentElement.dataset.motion === "reduced") return true;
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Any page that scrolls has a way back (owner, 2026-09-21; gairm/lms#215 —
 * Starsach's #243 shape): a round button, bottom right, 44 px, its name in
 * words, that appears once the page has moved about a screen and goes back
 * to the top in one press — smoothly unless motion is reduced.
 *
 * Focus goes to the page's h1 (the one PageHeader renders, where RouteFocus
 * puts it after a navigation) rather than being dropped on <body> when the
 * button disappears: a keyboard or screen-reader user lands where a sighted
 * one looks.
 *
 * z-20, under the phone menu's backdrop (z-30) and rail (z-40): an open menu
 * covers it like the rest of the page. Later in the document than the page
 * banner (also z-20), so it paints above that.
 */
export function BackToTop({ threshold = 480 }: { threshold?: number }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const onScroll = () => setShown(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  if (!shown) return null;
  return (
    <button
      type="button"
      data-testid="back-to-top"
      onClick={() => {
        (document.querySelector<HTMLElement>("main h1") ?? document.getElementById("main"))?.focus({ preventScroll: true });
        window.scrollTo({ top: 0, behavior: reducedMotion() ? "auto" : "smooth" });
      }}
      className="fixed bottom-4 right-4 z-20 inline-flex min-h-11 items-center gap-2 rounded-full border border-line bg-surface px-4 text-sm font-medium text-ink shadow-md hover:bg-canvas"
    >
      <Icon name="arrow-up" className="size-4" />
      Back to top
    </button>
  );
}
